/**
 * MANTER A SESSÃO VIVA ENQUANTO A PESSOA USA O SISTEMA.
 *
 * ─── O PROBLEMA, MEDIDO ─────────────────────────────────────────────────────
 * 👑 O dono, 24/08: *"quando o usuario esta utilizando, ele simplesmente
 * desloga... nao quero que cx e ux do sistema sejam deslogados"*.
 *
 * `app_errors` já vinha gravando a causa desde 17/08 e ninguém tinha lido:
 *
 *     71 quedas de sessão em 14 dias
 *     49  API devolveu 401
 *     13  sessão sumiu sem pedir saída
 *      9  token venceu e NÃO foi renovado
 *
 * Uma delas, por extenso: *"token guardado vencido há 9min não foi renovado — o
 * boot caiu sem sessão (sessão estava viva desde 01h5x)"*, com a queda às 13h43.
 *
 * ─── POR QUE ACONTECE ───────────────────────────────────────────────────────
 * O token dura ~1h e o supabase-js agenda um RELÓGIO pra trocá-lo antes de
 * vencer. **O navegador congela relógios de aba em segundo plano e de máquina
 * dormindo** — é assim que ele poupa bateria. O relógio não toca, o token vence,
 * e a próxima chamada leva 401.
 *
 * 🔑 Ou seja: o defeito não tem NADA a ver com subir versão. Ele acontece com o
 * sistema parado, e o inimigo é o tempo.
 *
 * ─── A REGRA QUE O DONO PEDIU ───────────────────────────────────────────────
 * *"cada ação que o usuário tem, renova por mais x horas... o relógio recomeça
 * quando o usuário utiliza o sistema"*. É sessão deslizante: **enquanto houver
 * uso, não cai.**
 *
 * ⚠️ E o que ela NÃO é: isto não estende a sessão de quem foi embora. Sem uso,
 * o token vence normalmente — segurança intacta. O que muda é que **quem está
 * trabalhando não é interrompido.**
 */

/** Quanto antes do vencimento já vale renovar. 5 min cobre relógio fora de hora. */
export const MARGEM_MS = 5 * 60 * 1000;

/** Piso entre duas renovações, pra atividade contínua não virar enxurrada de rede. */
export const INTERVALO_MINIMO_MS = 60 * 1000;

/**
 * Decide se vale renovar AGORA. Pura — é a regra inteira, testável sem navegador.
 *
 * @param expiraEmMs  quando o token vence (epoch ms). `null` = não sei → renova,
 *                    porque não saber é o caso em que mais se erra ficando parado.
 * @param agoraMs     relógio
 * @param ultimaMs    quando renovou pela última vez (epoch ms), ou 0
 */
export function precisaRenovar(
  expiraEmMs: number | null,
  agoraMs: number,
  ultimaMs: number,
): boolean {
  // Acabou de renovar: não repete, mesmo que a pessoa clique 40 vezes seguidas.
  if (agoraMs - ultimaMs < INTERVALO_MINIMO_MS) return false;
  if (expiraEmMs === null) return true;
  return expiraEmMs - agoraMs <= MARGEM_MS;
}

/**
 * O `expires_at` do Supabase vem em SEGUNDOS. Converter errado dá um número
 * ~1000× menor que o relógio, e a conta diria "venceu em 1970" — renovaria a
 * cada clique. Fica numa função só, com nome, pra não se repetir espalhado.
 */
export const expiraEmMs = (expiresAtSegundos: number | null | undefined): number | null =>
  typeof expiresAtSegundos === "number" && Number.isFinite(expiresAtSegundos)
    ? expiresAtSegundos * 1000
    : null;

/** Eventos que contam como "a pessoa está usando o sistema". */
export const EVENTOS_DE_USO = ["click", "keydown", "pointerdown", "focus"] as const;

// ────────────────────────────────────────────────────────────────────────────
// A MONTAGEM — ligar a regra aos eventos do navegador
// ────────────────────────────────────────────────────────────────────────────
/**
 * 🔑 Por que isto NÃO mora dentro do `useEffect`.
 *
 * A regra (`precisaRenovar`) já era testável. A MONTAGEM não era — e a lição que
 * mais custou nesta casa é que **testar a peça não testa a montagem**: a peça
 * fica verde enquanto ninguém a chama, ou enquanto a chamam no evento errado.
 *
 * Tirando a montagem de dentro do React, ela vira código comum, com alvos
 * injetáveis, e o portão consegue conferir o que de fato importa: que TODO
 * evento de uso está ligado, que a volta pra aba conta, e que **tudo o que foi
 * pendurado é despendurado** — listener que sobra vira renovação fantasma de uma
 * sessão que já saiu.
 */
export interface AlvoDeEventos {
  addEventListener(tipo: string, ouvinte: () => void, opcoes?: unknown): void;
  removeEventListener(tipo: string, ouvinte: () => void, opcoes?: unknown): void;
}

export function ligarRenovacaoPeloUso(opcoes: {
  janela: AlvoDeEventos;
  documento: AlvoDeEventos & { visibilityState?: string };
  /** O que fazer quando houve uso. A decisão de renovar (ou não) é de quem passa. */
  aoUsar: () => void;
}): () => void {
  const { janela, documento, aoUsar } = opcoes;

  // Voltar pra aba só conta quando ela ficou VISÍVEL. `visibilitychange` também
  // dispara ao esconder, e renovar ao sair é gastar rede por quem foi embora.
  const aoVoltar = () => {
    if (documento.visibilityState === "visible") aoUsar();
  };

  documento.addEventListener("visibilitychange", aoVoltar);
  for (const ev of EVENTOS_DE_USO) {
    janela.addEventListener(ev, aoUsar, { passive: true, capture: true });
  }

  return () => {
    documento.removeEventListener("visibilitychange", aoVoltar);
    for (const ev of EVENTOS_DE_USO) {
      janela.removeEventListener(ev, aoUsar, { capture: true });
    }
  };
}
